// Columna izquierda del explorer (`.md-filters` del mock): Búsqueda · Decisión · Nivel · Proveedor
// · Thin file · Solo contradictorias. La búsqueda es local y se emite con debounce.
import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { ScoringDecisionEnum } from '@fiado/type-kit/bin/loanScoring/enums/ScoringDecisionEnum';
import { ScoringProviderEnum } from '@fiado/type-kit/bin/loanScoring/enums/ScoringProviderEnum';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { cn } from '@/lib/cn';
import {
    CLIENT_LEVEL_LABEL,
    countActiveFilters,
    emptyEvaluationFilters,
    SCORING_DECISION_LABEL,
    SCORING_PROVIDER_LABEL,
} from '../logic';
import { ANY_FILTER, type ModeliaEvaluationFilters, type ThinFileFilter } from '../types';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD =
    'h-[26px] w-full rounded border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring';

const DECISION_OPTIONS = Object.values(ScoringDecisionEnum).map((decision) => ({
    value: decision,
    label: SCORING_DECISION_LABEL[decision],
}));

/** De mejor a peor, que es como se lee una escalera de nivel — no alfabético. */
const LEVEL_OPTIONS: { value: ClientLevelEnum; label: string }[] = [
    ClientLevelEnum.GOLD,
    ClientLevelEnum.SILVER,
    ClientLevelEnum.BRONZE,
    ClientLevelEnum.NOT_ELIGIBLE,
].map((level) => ({ value: level, label: CLIENT_LEVEL_LABEL[level] }));

const PROVIDER_OPTIONS = Object.values(ScoringProviderEnum).map((provider) => ({
    value: provider,
    label: SCORING_PROVIDER_LABEL[provider],
}));

const THIN_FILE_OPTIONS: { value: ThinFileFilter; label: string }[] = [
    { value: ANY_FILTER, label: 'Todas' },
    { value: 'thin', label: 'Solo thin file' },
    { value: 'full', label: 'Solo con historial' },
];

export interface ModeliaFiltersPanelProps {
    filters: ModeliaEvaluationFilters;
    onChange: (next: ModeliaEvaluationFilters) => void;
    /** Que los filtros miran lo cargado y no el listado. `null` cuando ya no quedan páginas. */
    loadedNote?: string | null;
    /** `false` cuando ninguna fila cargada trae teléfono: la búsqueda no puede prometerlo. */
    phoneSearchable?: boolean;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}

export function ModeliaFiltersPanel({
    filters,
    onChange,
    loadedNote = null,
    phoneSearchable = false,
    collapsed,
    onToggleCollapsed,
}: ModeliaFiltersPanelProps) {
    const [searchText, setSearchText] = useState(filters.search);
    const debouncedSearch = useDebounce(searchText, 250);
    const emittedSearch = useRef(filters.search);

    // Sube la búsqueda ya reposada; el guard evita re-emitir cuando cambia otro filtro.
    useEffect(() => {
        if (debouncedSearch === emittedSearch.current) return;
        emittedSearch.current = debouncedSearch;
        onChange({ ...filters, search: debouncedSearch });
    }, [debouncedSearch, filters, onChange]);

    // Baja un reset hecho desde afuera (limpiar del padre) a la caja local.
    useEffect(() => {
        if (filters.search === emittedSearch.current) return;
        emittedSearch.current = filters.search;
        setSearchText(filters.search);
    }, [filters.search]);

    const patch = (partial: Partial<ModeliaEvaluationFilters>) => {
        onChange({ ...filters, ...partial });
    };

    const clearAll = () => {
        emittedSearch.current = '';
        setSearchText('');
        onChange(emptyEvaluationFilters());
    };

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={countActiveFilters(filters)}
            onReset={clearAll}
        >
            {loadedNote === null ? null : (
                <p className="mt-2 text-[10px] leading-snug text-muted-foreground">{loadedNote}</p>
            )}

            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        aria-hidden
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        type="text"
                        value={searchText}
                        onChange={(event) => {
                            setSearchText(event.target.value);
                        }}
                        placeholder={
                            phoneSearchable
                                ? 'Nombre, teléfono, evaluación o acreditado…'
                                : 'Nombre, evaluación o acreditado…'
                        }
                        aria-label={
                            phoneSearchable
                                ? 'Buscar por nombre o teléfono del acreditado, o por id de evaluación o de acreditado'
                                : 'Buscar por nombre del acreditado, o por id de evaluación o de acreditado'
                        }
                        className={cn(FIELD, 'pl-6 pr-6')}
                    />
                    {searchText === '' ? null : (
                        <button
                            type="button"
                            onClick={() => {
                                emittedSearch.current = '';
                                setSearchText('');
                                patch({ search: '' });
                            }}
                            aria-label="Limpiar búsqueda"
                            className="absolute right-1 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <X className="size-3" aria-hidden />
                        </button>
                    )}
                </div>
                {phoneSearchable ? null : (
                    <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
                        El teléfono no se puede buscar: el listado del motor todavía no lo trae.
                    </p>
                )}
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Decisión</div>
                <select
                    value={filters.decision}
                    onChange={(event) => {
                        patch({
                            decision: event.target.value as ModeliaEvaluationFilters['decision'],
                        });
                    }}
                    aria-label="Decisión"
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todas</option>
                    {DECISION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Nivel</div>
                <select
                    value={filters.level}
                    onChange={(event) => {
                        patch({
                            level: event.target.value as ModeliaEvaluationFilters['level'],
                        });
                    }}
                    aria-label="Nivel"
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todos</option>
                    {LEVEL_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Proveedor</div>
                <select
                    value={filters.provider}
                    onChange={(event) => {
                        patch({
                            provider: event.target.value as ModeliaEvaluationFilters['provider'],
                        });
                    }}
                    aria-label="Proveedor"
                    className={FIELD}
                >
                    <option value={ANY_FILTER}>Todos</option>
                    {PROVIDER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Thin file</div>
                <select
                    value={filters.thinFile}
                    onChange={(event) => {
                        patch({ thinFile: event.target.value as ThinFileFilter });
                    }}
                    aria-label="Thin file"
                    className={FIELD}
                >
                    {THIN_FILE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <label className={cn(GROUP, 'flex cursor-pointer items-start gap-2 text-[11.5px]')}>
                <input
                    type="checkbox"
                    checked={filters.onlyContradictory}
                    onChange={(event) => {
                        patch({ onlyContradictory: event.target.checked });
                    }}
                    className="mt-px size-3.5 flex-none accent-destructive"
                />
                <span>
                    Solo contradictorias
                    <span className="mt-0.5 block text-[10px] leading-snug text-muted-foreground">
                        Aprobadas cuyo SCI o nivel no las sostiene.
                    </span>
                </span>
            </label>
        </FiltersColumn>
    );
}
